import type { CmsAdapter, CmsPage } from '@eneco/cms-adapter';
import { type Link, linkWithUrl } from '@eneco/cms-types';

/** The first candidate that is a non-empty string, as is: the legacy `Route`'s `a || b` over string fields. */
const legacyText = (...candidates: unknown[]): string | undefined =>
  candidates.find((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0);

/**
 * The `<title>` for a CMS page.
 *
 * As the legacy `Route` picks it: Sitecore's "Browser Title" (`browserTitle`), else the page's
 * `pageTitle` where its template has one, else `fallback`, the container's `label` on both lanes. The
 * editorial `title` and the open graph title are not used, and the value is not trimmed, as on legacy.
 *
 * Page-document fields land in `root.content` (`cms-sanity/src/adapter.ts`'s `buildTree` keeps every
 * non-slot field there), which is why this reads from content rather than needing a separate query.
 */
export const pageTitle = (page: CmsPage, fallback: string): string => {
  const content = page.root.content as Record<string, unknown> | undefined;
  const seo = (content?.seo ?? {}) as Record<string, unknown>;

  return legacyText(seo.title, content?.browserTitle, content?.pageTitle) ?? fallback;
};

/** The `seo` object every page document carries (ADR-0054). Normalised, so preferred over the
 *  individual Sitecore fields it was derived from. */
interface PageSeo {
  title?: string;
  description?: string;
  image?: unknown;
  noIndex?: boolean;
}

/** One of the page's language versions, itself included, with its absolute URL. */
export interface PageAlternate {
  hrefLang: string;
  href: string;
}

/** What the legacy `Route` puts in the head. */
export interface PageHeadMeta {
  title: string;
  description?: string;
  keywords?: string;
  /** Always set, as `Route` emits it: `index, follow` unless the page opts out. */
  robots: string;
}

/** What Sitecore's `MetaData` rendering puts in the head. */
export interface PageMetaDataTags {
  canonical?: string;
  /** One per language version; only the Sanity adapter supplies these. */
  alternates: PageAlternate[];
  openGraph: { title?: string; description?: string; image?: string };
}

const text = (...candidates: unknown[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim();
  }
  return undefined;
};

interface LanguageVersion {
  language: string;
  href: string;
  current: boolean;
}

/** `languageVersions`, as `cms-sanity`'s adapter puts it on the page (`withLanguageVersionUrls`). */
const languageVersions = (value: unknown): LanguageVersion[] =>
  Array.isArray(value)
    ? value.filter(
        (version): version is LanguageVersion =>
          typeof version?.language === 'string' && typeof version?.href === 'string' && version.href.length > 0,
      )
    : [];

const contentOf = (page: CmsPage) => {
  const content = (page.root.content ?? {}) as Record<string, unknown>;
  return { content, seo: (content.seo ?? {}) as PageSeo };
};

/**
 * The tags the legacy `Route` emits, read off the page document: the title ({@link pageTitle}),
 * `robots` on every page, and the meta description and keywords where authored, as is.
 *
 * Page-document fields land in `root.content`, so all of this is already on the `CmsPage`. `seo` wins
 * over the individual fields wherever both exist: it is the page contract the older migration wrote,
 * and the loose fields are the Sitecore originals it was derived from.
 */
export const pageHead = (page: CmsPage, fallback: string): PageHeadMeta => {
  const { content, seo } = contentOf(page);

  const noIndex = seo.noIndex === true || content.doNotIndex === true;
  const noFollow = content.doNotFollow === true;

  return {
    title: pageTitle(page, fallback),
    description: legacyText(seo.description, content.metaDescription),
    keywords: legacyText(content.metaKeywords),
    robots: `${noIndex ? 'noindex' : 'index'}, ${noFollow ? 'nofollow' : 'follow'}`,
  };
};

/**
 * The tags Sitecore's `MetaDataContentResolver` computes for the `MetaData` rendering, from a Sanity
 * page's own fields: the canonical is the page's own absolute URL unless one is authored, one `hreflang`
 * link per language version (the page alone when it has no translations, as legacy emits it), and the
 * open graph tags. `og:title` is the open graph title, else the browser title; `og:description` the
 * open graph description, else the meta description; `og:image` the open graph image, else the page's
 * `image` (ADR-0104; only articles and product pages have one), as on the legacy lane.
 *
 * `canonicalUrl` and the images go through the adapter because they are a link object and images
 * respectively, not plain strings.
 */
export const pageMetaData = (page: CmsPage, adapter: CmsAdapter): PageMetaDataTags => {
  const { content, seo } = contentOf(page);
  const image = adapter.toImage(content.openGraphImage) ?? adapter.toImage(seo.image) ?? adapter.toImage(content.image);
  const versions = languageVersions(content.languageVersions);

  return {
    canonical:
      linkWithUrl(content.canonicalUrl as Link | undefined)?.href ?? versions.find(version => version.current)?.href,
    alternates: versions.map(({ language, href }) => ({ hrefLang: language, href })),
    openGraph: {
      title: text(content.openGraphTitle, seo.title, content.browserTitle),
      description: text(content.openGraphDescription, seo.description, content.metaDescription),
      image: image?.src,
    },
  };
};
